import React, { useState, useEffect } from 'react'
import { 
  RotateCcw, 
  Download, 
  Upload, 
  Trash2, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Database,
  FileText,
  X,
  RefreshCw
} from 'lucide-react'
import { dataRecoveryTool } from '../utils/dataRecoveryTool'
import toast from 'react-hot-toast'

const DataRecoveryModal = ({ isOpen, onClose, storyId, onRecover }) => {
  const [dataSources, setDataSources] = useState([])
  const [selectedSource, setSelectedSource] = useState(null)
  const [loading, setLoading] = useState(false)
  const [recoveryReport, setRecoveryReport] = useState(null)

  // 加载数据源
  const loadDataSources = () => {
    const sources = dataRecoveryTool.scanAllDataSources()
    const storySources = storyId ? 
      sources.filter(source => source.storyId === storyId) : 
      sources
    
    setDataSources(storySources)
    
    if (storyId) {
      const report = dataRecoveryTool.generateRecoveryReport(storyId)
      setRecoveryReport(report)
    }
  }

  // 初始化加载
  useEffect(() => {
    if (isOpen) {
      loadDataSources()
    }
  }, [isOpen, storyId])

  // 恢复数据
  const handleRecover = async (sourceKey) => {
    setLoading(true)
    try {
      const result = await dataRecoveryTool.restoreFromSource(sourceKey)
      
      if (result.success) {
        toast.success('数据恢复成功！')
        if (onRecover) {
          onRecover(result.data)
        }
        onClose()
      } else {
        toast.error(`恢复失败: ${result.error}`)
      }
    } catch (error) {
      console.error('恢复数据失败:', error)
      toast.error('恢复数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 创建备份
  const handleCreateBackup = async (sourceKey) => {
    try {
      const data = JSON.parse(localStorage.getItem(sourceKey))
      const storyId = dataRecoveryTool.extractStoryId(sourceKey)
      const success = dataRecoveryTool.createBackup(storyId, data)
      
      if (success) {
        toast.success('备份创建成功！')
        loadDataSources() // 重新加载数据源
      } else {
        toast.error('备份创建失败')
      }
    } catch (error) {
      console.error('创建备份失败:', error)
      toast.error('创建备份失败')
    }
  }

  // 删除数据源
  const handleDeleteSource = (sourceKey) => {
    if (confirm('确定要删除这个数据源吗？此操作不可恢复！')) {
      localStorage.removeItem(sourceKey)
      toast.success('数据源已删除')
      loadDataSources() // 重新加载数据源
    }
  }

  // 清理过期数据
  const handleCleanup = () => {
    if (confirm('确定要清理过期数据吗？这将删除7天前的备份和恢复数据。')) {
      const cleanedCount = dataRecoveryTool.cleanupExpiredData()
      toast.success(`已清理 ${cleanedCount} 个过期数据项`)
      loadDataSources() // 重新加载数据源
    }
  }

  // 获取数据源图标
  const getDataSourceIcon = (type) => {
    switch (type) {
      case '用户编辑数据': return <FileText className="w-5 h-5 text-green-500" />
      case 'AI生成数据': return <Database className="w-5 h-5 text-blue-500" />
      case '备份数据': return <Download className="w-5 h-5 text-purple-500" />
      case '恢复数据': return <RotateCcw className="w-5 h-5 text-orange-500" />
      default: return <Database className="w-5 h-5 text-gray-500" />
    }
  }

  // 格式化时间
  const formatTime = (timeString) => {
    try {
      return new Date(timeString).toLocaleString('zh-CN')
    } catch {
      return timeString
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-dark rounded-2xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <RotateCcw className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-white">数据恢复工具</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadDataSources}
              className="p-2 hover:bg-white/10 rounded transition-colors"
              title="刷新数据源"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 恢复建议 */}
        {recoveryReport && recoveryReport.recommendations.length > 0 && (
          <div className="p-4 bg-blue-500/10 border-b border-blue-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h3 className="text-blue-400 font-semibold mb-2">恢复建议</h3>
                <ul className="space-y-1">
                  {recoveryReport.recommendations.map((rec, index) => (
                    <li key={index} className="text-sm text-blue-300">
                      • {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* 数据源列表 */}
        <div className="flex-1 overflow-auto p-6">
          {dataSources.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Database className="w-16 h-16 mb-4" />
              <p className="text-lg">没有找到可恢复的数据</p>
              <p className="text-sm">请检查是否有其他故事的数据源</p>
            </div>
          ) : (
            <div className="space-y-4">
              {dataSources.map((source, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border transition-all duration-200 ${
                    selectedSource === source.key
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getDataSourceIcon(source.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-white truncate">
                            {source.title}
                          </h3>
                          <span className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded">
                            {source.type}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-400">
                          <div>
                            <span className="text-gray-500">大小:</span>
                            <span className="ml-1">{source.sizeKB} KB</span>
                          </div>
                          <div>
                            <span className="text-gray-500">节点:</span>
                            <span className="ml-1">{source.nodeCount}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">连接:</span>
                            <span className="ml-1">{source.edgeCount}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">修改时间:</span>
                            <span className="ml-1">{formatTime(source.lastModified)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-2 text-xs text-gray-500">
                          数据源: {source.key}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setSelectedSource(
                          selectedSource === source.key ? null : source.key
                        )}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                          selectedSource === source.key
                            ? 'bg-primary text-white'
                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        }`}
                      >
                        {selectedSource === source.key ? '已选择' : '选择'}
                      </button>
                    </div>
                  </div>
                  
                  {selectedSource === source.key && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRecover(source.key)}
                          disabled={loading}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          <RotateCcw className="w-4 h-4" />
                          {loading ? '恢复中...' : '恢复数据'}
                        </button>
                        
                        <button
                          onClick={() => handleCreateBackup(source.key)}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          创建备份
                        </button>
                        
                        <button
                          onClick={() => handleDeleteSource(source.key)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          删除
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部操作 */}
        <div className="p-6 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              找到 {dataSources.length} 个数据源
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleCleanup}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                清理过期数据
              </button>
              
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DataRecoveryModal
